import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  SparklesIcon,
  UserPlusIcon,
  RocketLaunchIcon 
} from '@heroicons/react/24/outline';

const OnboardingSuccess = ({ formData, onFinish }) => {
  const nextSteps = [
    {
      icon: UserPlusIcon,
      title: 'Invite your team',
      description: 'Add team members to start collaborating on projects',
      action: 'Invite team'
    },
    {
      icon: SparklesIcon,
      title: 'Set up your templates',
      description: 'Customize workflows for your photography projects',
      action: 'Configure templates'
    },
    {
      icon: RocketLaunchIcon,
      title: 'Create your first project',
      description: 'Start managing clients and shoots with Klorah',
      action: 'Create project'
    }
  ];

  return (
    <div className="px-8 py-12 max-w-4xl mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="mb-12"
      >
        <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#FFB27D] to-[#FF9D5C] flex items-center justify-center shadow-2xl shadow-orange-500/30">
          <CheckCircleIcon className="w-12 h-12 text-white" />
        </div>
        
        <h2 className="text-5xl font-bold text-white mb-6">
          Welcome to {formData.organizationName}!
        </h2>
        
        <div className="space-y-3 text-white/70 text-xl">
          <p>Your {formData.plan === 'free' ? 'Free' : 'Professional'} workspace is ready.</p>
          {formData.plan === 'professional' && (
            <p className="text-brand font-medium text-2xl">
              ✨ Your subscription has started
            </p>
          )}
        </div>
      </motion.div>

      {/* What's Included */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/5 rounded-2xl p-8 mb-12 border border-white/10"
      >
        <h3 className="text-2xl font-semibold text-white mb-6">
          What's included in your workspace
        </h3>
        <div className="grid grid-cols-2 gap-6 text-base">
          {formData.templates?.map((templateId) => (
            <div key={templateId} className="flex items-center gap-3">
              <CheckCircleIcon className="w-5 h-5 text-brand" />
              <span className="text-white/90 capitalize">
                {templateId.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Next Steps */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-12"
      >
        <h3 className="text-2xl font-semibold text-white mb-8">
          Get started with these next steps
        </h3>
        <div className="space-y-6">
          {nextSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="flex items-center gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-brand hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-300 group cursor-pointer"
            >
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#FFB27D] to-[#FF9D5C] shadow-lg">
                <step.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-semibold text-white text-lg group-hover:text-brand transition-colors">
                  {step.title}
                </h4>
                <p className="text-base text-white/70 mt-1">
                  {step.description}
                </p>
              </div>
              <button className="px-6 py-3 text-base font-medium text-brand hover:bg-brand/10 rounded-xl transition-colors">
                {step.action}
              </button>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Main CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <button
          onClick={onFinish}
          className="px-12 py-4 bg-brand hover:bg-brand-hover text-black font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl text-lg"
        >
          Enter your workspace
        </button>
        
        {formData.plan === 'professional' && (
          <p className="text-base text-white/50 mt-6">
            Remember: You can cancel anytime.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingSuccess;